'use client';

import { useCallback } from 'react';

import { logger } from '@/lib/logger';

const withoutHiddenContent = (node: HTMLElement): HTMLElement => {
  const clone = node.cloneNode(true) as HTMLElement;
  clone
    .querySelectorAll('[aria-hidden="true"]')
    .forEach(hidden => hidden.remove());
  return clone;
};

const hasAccessibleName = (node: HTMLElement): boolean => {
  if (
    node.getAttribute('aria-label')?.trim() ||
    node.getAttribute('aria-labelledby')?.trim() ||
    node.getAttribute('title')?.trim()
  ) {
    return true;
  }

  const visible = withoutHiddenContent(node);
  return (
    Boolean(visible.textContent?.trim()) ||
    Boolean(visible.querySelector('img[alt]:not([alt=""])'))
  );
};

export const useAccessibleNameWarning = (
  component: string,
): ((node: HTMLElement | null) => void) =>
  useCallback(
    (node: HTMLElement | null) => {
      if (process.env.NODE_ENV === 'production' || !node) return;
      if (node.getAttribute('aria-hidden') === 'true') return;
      if (hasAccessibleName(node)) return;

      logger.warn(
        `[${component}] Missing accessible name: add visible text, aria-label or aria-labelledby, or aria-hidden="true" if decorative`,
        { component },
      );
    },
    [component],
  );
